import type { Diagnostic } from '../types';

import { ActionRequiredError } from './errors';
import { diag } from './ruleHelpers';

/**
 * The shape of an equipment placeholder in an example routine: an
 * angle-bracketed upper-case kind name with an optional numeric suffix
 * (`<GRIPPER>`, `<WELDING_TORCH>`, `<WELD_MACHINE_2>`). The example routines
 * carry these in place of real equipment ids, and the agent is expected to
 * resolve each against the cell's equipment before saving.
 *
 * Matches the shape, not the vocabulary — the vocabulary lives with the
 * goldens (`apps/agent-bot/src/goldens/sentinels.ts`), which this library
 * must not import.
 */
const EXAMPLE_PLACEHOLDER_PATTERN =
  /^<[A-Z][A-Z0-9]*(?:_[A-Z][A-Z0-9]*)*(?:_[0-9]+)?>$/;

/** Whether a value looks like a copied example-routine placeholder. */
export function isExamplePlaceholder(value: unknown): value is string {
  return typeof value === 'string' && EXAMPLE_PLACEHOLDER_PATTERN.test(value);
}

export const EXAMPLE_PLACEHOLDER_MESSAGE =
  "This step still has an example placeholder instead of real equipment. Select this cell's equipment for this field.";

/**
 * Saved robot skills are elided the same way, but the remedy is a skill on
 * this robot, not a device, so the equipment message would misdirect.
 */
export const SKILL_PLACEHOLDER_MESSAGE =
  "This step still has an example placeholder instead of a saved skill. Select one of this robot's skills.";

/**
 * The placeholder diagnostic, for rules that assemble diagnostics inline
 * instead of throwing `ActionRequiredError`: error severity,
 * `ui.examplePlaceholder`, the standing operator message.
 */
export function examplePlaceholderDiag(
  stepId: string,
  fieldId: string,
  sourceRule: string,
): Diagnostic {
  return diag({
    code: 'ui.examplePlaceholder',
    message: EXAMPLE_PLACEHOLDER_MESSAGE,
    severity: 'error',
    stepId,
    fieldId,
    sourceRule,
  });
}

/**
 * Reject a copied example-routine placeholder before any equipment
 * exists-check runs. Without this, a placeholder falls through to the
 * equipment lookup and is reported as equipment that was removed from the
 * robot — a situation that never happened.
 *
 * @throws {ActionRequiredError}
 */
export function assertNotExamplePlaceholder(
  value: unknown,
  fieldId: string,
  message: string = EXAMPLE_PLACEHOLDER_MESSAGE,
): void {
  if (!isExamplePlaceholder(value)) return;

  throw new ActionRequiredError({
    kind: 'examplePlaceholder',
    message,
    fieldId,
  });
}
